import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  CheckIcon,
  ChevronDownIcon,
  Grid3x3Icon,
  PlusIcon,
  SearchIcon,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import { matrixItemMatchesSearch } from "@/domains/matrix";
import type { MatrixListItemUi } from "@/domains/matrix";
import { HINT_DURATION_MS } from "../lib/hint-duration";
import { useMatrixRailCollapsed } from "../lib/matrix-rail-state";
import { useMatrixRowActions } from "../hooks/use-matrix-row-actions";
import { MatrixActionsMenu } from "./matrix-actions-menu";
import { MatrixRowActionDialogs } from "./matrix-row-action-dialogs";

type MatrixTitleMenuProps = {
  name: string;
  matrices: MatrixListItemUi[];
  activeCollectionId: string;
  onCreate: () => void;
};

// The title doubles as the matrix switcher while the rail is out of reach.
export function MatrixTitleMenu({
  name,
  matrices,
  activeCollectionId,
  onCreate,
}: MatrixTitleMenuProps) {
  const navigate = useNavigate();
  const actions = useMatrixRowActions(matrices);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isRailCollapsed] = useMatrixRailCollapsed();
  const [wasRailCollapsed, setWasRailCollapsed] = useState(isRailCollapsed);
  const [isHintOpen, setIsHintOpen] = useState(false);

  if (wasRailCollapsed !== isRailCollapsed) {
    setWasRailCollapsed(isRailCollapsed);
    setIsHintOpen(isRailCollapsed);
  }

  useEffect(() => {
    if (!isHintOpen) return;

    function dismiss() {
      setIsHintOpen(false);
    }

    const timer = setTimeout(dismiss, HINT_DURATION_MS);
    document.addEventListener("pointermove", dismiss, { once: true });
    return () => {
      clearTimeout(timer);
      document.removeEventListener("pointermove", dismiss);
    };
  }, [isHintOpen]);

  const visibleMatrices = matrices.filter((matrix) =>
    matrixItemMatchesSearch(matrix, search),
  );

  function handleOpenChange(open: boolean) {
    setIsOpen(open);
    if (open) setIsHintOpen(false);
    if (!open) setSearch("");
  }

  function goTo(collectionId: string) {
    setIsOpen(false);
    if (collectionId !== activeCollectionId) {
      void navigate(`/matrix/${collectionId}`);
    }
  }

  return (
    <>
      <MatrixRowActionDialogs
        {...actions.dialogProps}
        matrices={matrices}
        activeCollectionId={activeCollectionId}
      />
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        {/* Forced open only for the fold hint, forced shut while the menu is,
            and left to itself in between: controlling it on every hover armed
            the hint's pointermove dismissal, which took the bubble away again
            on the next twitch. The delay skips titles that are only crossed. */}
        <Tooltip
          // Remounted after the hint: one held open from outside never reopens on hover.
          key={isHintOpen ? "hint" : "hover"}
          open={isOpen ? false : isHintOpen ? true : undefined}
          delayDuration={400}
        >
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`${name}. Switch matrix`}
                className="hover:bg-background hover:border-border/70 focus-visible:ring-ring/50 data-[state=open]:bg-background data-[state=open]:border-border/70 -ml-2 flex h-8 min-w-0 cursor-pointer items-center gap-1.5 rounded-md border border-transparent px-2 text-left transition-colors hover:shadow-xs focus-visible:ring-[3px] focus-visible:outline-none"
              >
                <span className="min-w-0 truncate text-base font-semibold tracking-tight">
                  {name}
                </span>
                <ChevronDownIcon className="text-muted-foreground size-4 shrink-0" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Switch, search or add a matrix
          </TooltipContent>
        </Tooltip>

        <PopoverContent
          align="start"
          className="w-72 overflow-hidden rounded-xl p-0"
        >
          <div className="border-b p-2">
            <div className="relative">
              <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                autoFocus
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search matrices..."
                aria-label="Search matrices"
                className="h-8 pl-9"
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto p-1">
            {visibleMatrices.map((matrix) => {
              const isActive = matrix.collectionId === activeCollectionId;

              return (
                <div
                  key={matrix.collectionId}
                  className="group hover:bg-muted relative flex items-center rounded-md transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => goTo(matrix.collectionId)}
                    className={cn(
                      "focus-visible:ring-ring/50 flex min-w-0 flex-1 items-center gap-2 rounded-md py-1.5 pr-9 pl-2 text-left text-sm focus-visible:ring-[3px] focus-visible:outline-none",
                      isActive && "text-primary font-medium",
                    )}
                  >
                    <Grid3x3Icon
                      className={cn(
                        "size-4 shrink-0",
                        isActive ? "text-primary" : "text-muted-foreground",
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {matrix.name}
                    </span>
                  </button>
                  {isActive ? (
                    <CheckIcon
                      aria-hidden
                      className="text-primary group-hovered:opacity-0 pointer-events-none absolute right-2.5 size-4 transition-opacity"
                    />
                  ) : null}
                  <MatrixActionsMenu
                    {...actions.menuPropsFor(matrix)}
                    align="end"
                    triggerClassName="absolute right-1 opacity-0 group-hovered:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                  />
                </div>
              );
            })}

            {visibleMatrices.length === 0 ? (
              <p className="text-muted-foreground px-2 py-3 text-xs">
                No matrix matches "{search}".
              </p>
            ) : null}
          </div>

          <div className="border-t p-1">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 font-normal"
              onClick={() => {
                setIsOpen(false);
                onCreate();
              }}
            >
              <PlusIcon className="size-4" />
              New matrix
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
